import { useGame } from '../state/useGame';
import { SKILLS, SKILLS_BY_ID } from '../domain/skills/spec';
import { xpForLevel } from '../domain/skills/xp';
import { deriveTutorial } from '../domain/quests/tutorial';

const STEP_CTA_LABEL: Record<string, string> = {
  skills: 'Go to Skills',
  bank: 'Open the Bank',
  inventory: 'Open the Inventory',
  equipment: 'Open Equipment',
  quests: 'Open Quests',
  combat: 'Open Combat',
};

/** Herman's coach card: derived seven-beat onboarding, never blocking. */
function TutorialCoach() {
  const state = useGame((s) => s.state);
  const setPage = useGame((s) => s.setPage);
  const setActiveSkill = useGame((s) => s.setActiveSkill);
  const view = deriveTutorial(state);

  if (view.complete) return null;

  const current = view.steps.find((s) => s.id === view.current)!;
  const doneCount = view.steps.filter((s) => s.done).length;
  const waitingForTick = current.id === 'first-tick';
  const ctaLabel = waitingForTick
    ? 'Training in progress'
    : current.page === 'skills'
      ? 'Train Codex Whispering'
      : (STEP_CTA_LABEL[current.page] ?? 'Continue');

  return (
    <section
      className="card coach-card"
      aria-labelledby="tutorial-coach-title"
      data-tutorial-step={current.id}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <img
          src="/assets/icons/A2_herman_whisper_skill.jpg"
          alt="Herman mid-whisper"
          width={96}
          height={96}
          style={{ borderRadius: 8, imageRendering: 'pixelated', background: '#1a1d24', flexShrink: 0 }}
        />
        <div style={{ flex: 1 }}>
          <strong id="tutorial-coach-title" style={{ color: 'var(--accent-nous)' }}>
            Night-watch induction — step {doneCount + 1} of {view.steps.length}
          </strong>
          <p style={{ margin: '6px 0 8px', color: 'var(--ui-text)', fontSize: 13 }}>{current.label}</p>
          <ol className="coach-steps" style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12, color: 'var(--ui-text-dim)' }}>
            {view.steps.map((step) => (
              <li key={step.id} data-step-id={step.id} data-step-done={step.done}>
                {step.done ? '✓ ' : ''}
                {step.label}
              </li>
            ))}
          </ol>
          <button
            className="primary"
            disabled={waitingForTick}
            onClick={() =>
              current.page === 'skills'
                ? setActiveSkill('codex_whispering')
                : setPage(current.page)
            }
            aria-label={`${ctaLabel} — tutorial step ${doneCount + 1}`}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </section>
  );
}

function XpBar({ xp, level }: { xp: number; level: number }) {
  const baseAt = xpForLevel(level);
  const baseNext = xpForLevel(level + 1);
  const span = baseNext - baseAt || 1;
  const pct = Math.min(100, Math.max(0, ((xp - baseAt) / span) * 100));
  return (
    <div
      className="xp-bar"
      role="progressbar"
      aria-label={`Level ${level}, ${pct.toFixed(0)}% to next`}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function SkillsPage() {
  const state = useGame((s) => s.state);
  const active = state.active;
  const setActiveSkill = useGame((s) => s.setActiveSkill);
  const stopActive = useGame((s) => s.stopActive);

  return (
    <>
      <h1 className="page-title">Skills</h1>
      <p className="page-subtitle">
        Train a skill to grind XP. Pick one — only one trains at a time. Auto-grinds in
        the background while the tab is open. Offline catches up to 24 hours of progress
        on reload.
      </p>

      <TutorialCoach />

      {SKILLS.map((spec) => {
        const prog = state.skills[spec.id];
        const isActive = active.kind === 'train' && active.skillId === spec.id;
        return (
          <div className="card" key={spec.id}>
            <div className="skill-row">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {spec.name}{' '}
                  <span className="tag">{spec.ring}</span>
                  {isActive && <span className="tag" style={{ color: 'var(--pipeline-green)' }}>training</span>}
                </div>
                <div className="meta">
                  Lv {prog.level} · {prog.xp.toLocaleString()} XP · {spec.flavor}
                </div>
                <XpBar xp={prog.xp} level={prog.level} />
              </div>
              <div>
                {isActive ? (
                  <button
                    className="danger"
                    aria-label={`Stop training ${spec.name}`}
                    onClick={stopActive}
                  >
                    Stop
                  </button>
                ) : (
                  <button
                    className="primary"
                    onClick={() => setActiveSkill(spec.id)}
                    disabled={!SKILLS_BY_ID.has(spec.id)}
                    aria-label={`Train ${spec.name}`}
                  >
                    Train
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}